import React from 'react';
import ReactDOM from 'react-dom';

function OnGlance({ isOpen, onClose, role }) {
  if (!isOpen) return null;

  const handleApply = () => {
    if (role.link) {
      window.open(role.link, '_blank');
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-60 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-6">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/30 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative max-w-3xl bg-white rounded-xl shadow-lg">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-4">
              {role.company?.logo_url && (
                <img
                  className="w-12 h-12 rounded-sm object-contain bg-gray-50 p-1"
                  src={role.company.logo_url}
                  alt={`${role.company.company_name} logo`}
                />
              )}
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{role.name}</h3>
                {role.company?.company_name && (
                  <p className="mt-1 text-sm text-gray-600">{role.company.company_name}</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-hidden"
            >
              <span className="sr-only">Close</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto max-h-[calc(100vh-200px)]">
            <div className="space-y-6">
              {/* Quick Info */}
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                {role.location && (
                  <div className="flex items-center gap-1.5">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    {role.location}
                  </div>
                )}
                {role.work_model && (
                  <div className="flex items-center gap-1.5">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    {role.work_model}
                  </div>
                )}
              </div>

              {/* Description */}
              {role.description && (
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-2">Description</h4>
                  <div className="prose prose-sm max-w-none text-gray-600 max-h-[400px] overflow-y-auto pr-4 custom-scrollbar">
                    <p className="whitespace-pre-line">{role.description}</p>
                  </div>
                </div>
              )}

              {/* Skills */}
              {role.skills && role.skills.length > 0 && (
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-2">Required Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {role.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Categories */}
              {role.categories && role.categories.length > 0 && (
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-2">Categories</h4>
                  <div className="flex flex-wrap gap-2">
                    {role.categories.map((category, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Close
            </button>
            {role.link && (
              <button
                type="button"
                onClick={handleApply}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Apply Now{' '}
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.getElementById('modal-root')
  );
}

export default OnGlance;
